import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Globe, Newspaper, Trophy } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { UserMenu } from './UserMenu';
import { LoginModal } from './LoginModal';
import { ThemeToggle } from './ThemeToggle';

interface SimpleHeaderProps {
  isHomePage?: boolean;
}

const SimpleHeader: React.FC<SimpleHeaderProps> = ({ isHomePage = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { lang } = useParams<{ lang?: string }>();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const langDropdownRef = useRef<HTMLDivElement>(null);

  // Track scroll to hide announcement bar
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Extract language from URL path as fallback
  const pathParts = location.pathname.split('/').filter(part => part !== '');
  const langFromPath = pathParts.length > 0 && ['en', 'fr', 'de', 'it'].includes(pathParts[0])
    ? pathParts[0] as 'en' | 'fr' | 'de' | 'it'
    : 'en';

  const currentLang = (lang as 'en' | 'fr' | 'de' | 'it') || langFromPath;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target as Node)) {
        setShowLangDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const translations = {
    back: {
      en: 'Back',
      fr: 'Retour',
      de: 'Zurueck',
      it: 'Indietro'
    },
    announcement: {
      en: { badge: '1000+', text: 'Swiss players using the app', link: null, showLogo: false },
      fr: { badge: '1000+', text: 'Joueurs suisses sur l\'appli', link: null, showLogo: false },
      de: { badge: '1000+', text: 'Schweizer Spieler in der App', link: null, showLogo: false },
      it: { badge: '1000+', text: 'Giocatori svizzeri sull\'app', link: null, showLogo: false },
    },
  };
  
  const handleLanguageChange = (newLang: string) => {
    // Replace the current language in the path with the new language
    const pathParts = location.pathname.split('/').filter(part => part !== '');
    
    // Check if first part is a language code
    if (pathParts.length > 0 && ['en', 'fr', 'de', 'it'].includes(pathParts[0])) {
      // Replace existing language
      pathParts[0] = newLang;
    } else {
      // Add language at the beginning
      pathParts.unshift(newLang);
    }
    
    const newPath = '/' + pathParts.join('/');
    navigate(newPath);
  };
  
  const showAnnouncementBar = !isScrolled;
  const currentAnnouncement = translations.announcement[currentLang];

  const goBackDeterministic = () => {
    try {
      const lastPath = sessionStorage.getItem('ttstats:last_path');
      if (lastPath && lastPath !== location.pathname && lastPath.startsWith('/')) {
        navigate(lastPath);
        return;
      }
    } catch {
      // ignore
    }
    navigate(`/${currentLang}`);
  };

  return (
    <>
      <motion.div
        className="fixed top-0 left-0 w-full z-30"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{
          duration: 0.4,
          ease: [0.4, 0, 0.2, 1]
        }}
      >
        {/* Announcement Bar */}
        <motion.div
          className="bg-[#1C7AD6] text-white overflow-hidden will-change-[height]"
          animate={{ height: showAnnouncementBar ? 32 : 0 }}
          transition={{ duration: 0.15, ease: 'linear' }}
        >
          <div className="max-w-7xl mx-auto px-4 h-8 flex items-center justify-center">
            <div className="flex items-center gap-2.5">
              <span className="bg-white/20 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded">
                {currentAnnouncement.badge}
              </span>
              <span className="text-sm font-medium">
                {currentAnnouncement.text}
              </span>
            </div>
          </div>
        </motion.div>

        {/* Main Header */}
        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border-b border-gray-100 dark:border-gray-800 transition-colors duration-300">
          <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
          {/* Left side - Back button or Beta indicator */}
          <div className="flex items-center">
	            {!isHomePage ? (
	              <motion.div
	                initial={{ opacity: 0, x: -20 }}
	                animate={{ opacity: 1, x: 0 }}
	                transition={{ duration: 0.3, delay: 0.1 }}
	              >
	                <button
	                  onClick={goBackDeterministic}
	                  className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition group"
	                >
	                  <motion.div
	                    className="mr-1"
	                    whileHover={{ x: -2 }}
	                    transition={{ duration: 0.2 }}
	                  >
	                    <ArrowLeft className="h-4 w-4" />
	                  </motion.div>
	                  <span className="hidden md:inline">
	                    {translations.back[currentLang] || translations.back.en}
	                  </span>
	                </button>
	              </motion.div>
	            ) : (
	              // Empty div to maintain layout balance on homepage
	              <div className="w-16" />
            )}
          </div>
          
          {/* Spacer */}
          <div className="flex-1" />

          {/* Right side controls */}
          <div className="flex items-center gap-2">
            {/* Language Switcher with Globe Icon */}
            <motion.div
              ref={langDropdownRef}
              className="relative"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.4,
                delay: 0.1,
                ease: [0.4, 0, 0.2, 1]
              }}
            >
              <motion.button
                onClick={() => setShowLangDropdown(!showLangDropdown)}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Globe className="h-4 w-4" />
                <span className="text-xs font-medium uppercase">{currentLang}</span>
              </motion.button>

              <AnimatePresence>
                {showLangDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 mt-1 py-1 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 min-w-[100px] z-50"
                  >
                    {(['en', 'fr', 'de', 'it'] as const).map((langOption) => (
                      <button
                        key={langOption}
                        onClick={() => {
                          handleLanguageChange(langOption);
                          setShowLangDropdown(false);
                        }}
                        className={`w-full px-3 py-1.5 text-left text-xs font-medium transition-colors ${
                          currentLang === langOption
                            ? 'bg-[#1C7AD6] text-white'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {langOption === 'en' && 'English'}
                        {langOption === 'fr' && 'Français'}
                        {langOption === 'de' && 'Deutsch'}
                        {langOption === 'it' && 'Italiano'}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Theme Toggle */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.4,
                delay: 0.15,
                ease: [0.4, 0, 0.2, 1]
              }}
            >
              <ThemeToggle />
            </motion.div>

            {/* News Link */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.4,
                delay: 0.2,
                ease: [0.4, 0, 0.2, 1]
              }}
            >
              <Link
                to={`/${currentLang}/news`}
                className="flex items-center gap-1.5 px-2 py-1.5 text-gray-600 dark:text-gray-400 hover:text-[#1C7AD6] dark:hover:text-[#1C7AD6] hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <Newspaper className="h-4 w-4" />
              </Link>
            </motion.div>

            {/* Rankings Link */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.4,
                delay: 0.22,
                ease: [0.4, 0, 0.2, 1]
              }}
            >
              <Link
                to={`/${currentLang}/rankings`}
                title={currentLang === 'de' ? 'Rangliste' : currentLang === 'fr' ? 'Classement' : currentLang === 'it' ? 'Classifica' : 'Rankings'}
                className="flex items-center gap-1.5 px-2 py-1.5 text-gray-600 dark:text-gray-400 hover:text-[#1C7AD6] dark:hover:text-[#1C7AD6] hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <Trophy className="h-4 w-4" />
              </Link>
            </motion.div>

            {/* User Menu */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.4,
                delay: 0.25,
                ease: [0.4, 0, 0.2, 1]
              }}
            >
              <UserMenu onOpenLogin={() => setShowLoginModal(true)} currentLang={currentLang} />
            </motion.div>
          </div>
        </div>
        </div>
      </div>
    </motion.div>

    {/* Login Modal */}
    <LoginModal
      isOpen={showLoginModal}
      onClose={() => setShowLoginModal(false)}
      currentLang={currentLang}
    />
    </>
  );
};

export default SimpleHeader;
